import { useState, useRef, useEffect } from 'react';
import * as React from 'react';
import { useDropzone, DropzoneState } from 'react-dropzone';
import {
  useField,
  useForm,
  notEmpty,
  getValues,
  Field,
  FormError,
  validator,
} from '@shopify/react-form';
import type { PickerOption } from '@sdk/components/forms/types';
import gettext from '@sdk/lang/gettext';
import useSlingApi from '@sdk/api/useSlingApi';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import getNowDate from '@sdk/utils/getNowDate';
import type { User } from '@sdk/users/types/User';
import type { DocumentData } from '@sdk/users/types/DocumentData';

export enum SharedVisibilityOption {
  employeeAndEmployeesManagers = 'EMPLOYEE_AND_EMPLOYEES_MANAGERS',
  employeeOnly = 'EMPLOYEE_ONLY',
  employeesManagersOnly = 'EMPLOYEES_MANAGERS_ONLY',
}

export type DocumentDetailsFormData = {
  formFields: {
    name: Field<string>;
    expirationDate: Field<Date>;
    description: Field<string>;
    isShared: Field<boolean>;
    sharedVisibilityOption: Field<SharedVisibilityOption>;
    expirationNotificationsEnabled: Field<boolean>;
  };
  submitForm: (event?: React.FormEvent) => void;
  submitErrors: FormError[];
  getValues: () => any;
  isValid: boolean;
  validate: () => void;
  dropzoneState: DropzoneState;
  selectedFile: File;
  isFileUpdating: boolean;
  clearFile: () => void;
  visibilityOptions: PickerOption[];
  isFormDirty: boolean;
  resetForm: () => void;
  onSaveHandle: () => Promise<void>;
  onFileClick: () => void;
  downloadFormRef: React.MutableRefObject<any>;
  documentPreviewRef: React.MutableRefObject<any>;
  discardChanges: () => void;
};

export const useDocumentDetails = (
  user: User,
  goBackToList: () => void,
  documentToEdit: DocumentData
): DocumentDetailsFormData => {
  const downloadFormRef = useRef(null);
  const documentPreviewRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState<File>(null);
  const [isFileUpdating, setIsFileUpdating] = useState(false);

  const currentFile =
    documentToEdit && !isFileUpdating ? documentToEdit.fileName : selectedFile?.name;
  const {
    fields: formFields,
    submit: submitForm,
    submitErrors,
    validate,
    dirty,
    reset,
  } = useForm({
    fields: {
      file: useField({
        value: currentFile,
        validates: [notEmpty(gettext('File is required'))],
      }),
      name: useField({
        value: documentToEdit?.name ?? '',
        validates: [notEmpty(gettext('Provide name'))],
      }),
      expirationDate: useField({
        value: documentToEdit?.expirationDate ?? null,
        validates: documentToEdit?.expirationDate
          ? [
              validator((value: Date): boolean => {
                if (documentToEdit.isExpired) {
                  return value > getNowDate();
                }

                return true; // true means it's valid
              })(gettext('Update an expiration date')),
            ]
          : [],
      }),
      description: useField(documentToEdit?.description ?? ''),
      isShared: useField(documentToEdit?.isShared ?? false),
      sharedVisibilityOption: useField<SharedVisibilityOption>(
        (documentToEdit?.sharedVisibilityOption as SharedVisibilityOption) ??
          SharedVisibilityOption.employeeAndEmployeesManagers
      ),
      expirationNotificationsEnabled: useField(
        documentToEdit?.expirationNotificationsEnabled ?? true
      ),
    },
  });

  const dropzoneState = useDropzone({
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      setSelectedFile(acceptedFiles[0]);
      validate();
    },
  });

  const api = useSlingApi();
  const [isSaving, setIsSaving] = useState(false);
  const { showErrorToast } = useToast();

  const clearFile = () => {
    setSelectedFile(null);
    setIsFileUpdating(true);
  };

  const visibilityOptions: PickerOption[] = [
    {
      label: gettext("Both to the employee and employee's managers"),
      value: SharedVisibilityOption.employeeAndEmployeesManagers,
    },
    {
      label: gettext('Only to the employee'),
      value: SharedVisibilityOption.employeeOnly,
    },
    {
      label: gettext("Only to employee's managers"),
      value: SharedVisibilityOption.employeesManagersOnly,
    },
  ];

  const isFormValid = () => Object.values(formFields).every((f) => !f.error);
  const isValid = isFormValid();
  const formFieldValues = Object.values(formFields).map((field) => field.value);

  useEffect(() => {
    validate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validate, ...formFieldValues]);

  const onSaveHandle = async () => {
    if (isSaving) {
      return;
    }

    submitForm();

    if (!isValid) {
      throw 'Invalid form';
    }

    try {
      setIsSaving(true);
      if (documentToEdit) {
        const values = getValues(formFields);
        await api.users.updateDocument(documentToEdit, selectedFile, values);
      } else {
        await api.users.addDocument(user, selectedFile, getValues(formFields));
      }
      goBackToList();
      // no need to set isSaving to false as we're navigating away
      // from this view and the component will get destroyed
    } catch (e) {
      showErrorToast(getErrorForToast(e));
      setIsSaving(false);
    }
  };

  const onFileClick = () => {
    if (documentToEdit?.mimetype?.startsWith('image')) {
      documentPreviewRef?.current.open();
    } else {
      downloadFormRef?.current.submit();
    }
  };

  const discardChanges = () => {
    reset();
    setSelectedFile(null);
    setIsFileUpdating(false);
  };

  return {
    formFields,
    submitForm,
    submitErrors,
    validate,
    getValues: () => getValues(formFields),
    isValid,
    dropzoneState,
    selectedFile,
    isFileUpdating,
    clearFile,
    visibilityOptions,
    isFormDirty: dirty,
    resetForm: reset,
    onSaveHandle,
    onFileClick,
    downloadFormRef,
    documentPreviewRef,
    discardChanges,
  };
};
